import { BaseModel, type RelationConfig } from './base';

import { Customer } from './customer';
import { Reservation } from './reservation';
import { ReservationCustomer } from './reservationCustomer';

// Enums

export enum CourseRunMakeupMode {
  none = 0,
  anyService = 1,
  anyRun = 2,
  selectRuns = 2,
}

export const CourseRunMakeupModeLabels: Record<CourseRunMakeupMode, string> = {
  [CourseRunMakeupMode.none]: 'general.none',
  [CourseRunMakeupMode.anyService]: 'course.anyService',
  [CourseRunMakeupMode.anyRun]: 'course.anyRun',
};

export enum CourseRunPaymentMode {
  full = 0,
  instalments = 1,
}

export const CourseRunPaymentModeLabels: Record<CourseRunPaymentMode, string> = {
  [CourseRunPaymentMode.full]: 'course.full',
  [CourseRunPaymentMode.instalments]: 'course.instalments',
};

export enum CourseRunPartialMode {
  none = 0,
  late = 1,
  select = 2,
}

export const CourseRunPartialModeLabels: Record<CourseRunPartialMode, string> = {
  [CourseRunPartialMode.none]: 'general.none',
  [CourseRunPartialMode.late]: 'course.late',
  [CourseRunPartialMode.select]: 'course.select',
};

export enum CourseRunStatus {
  confirmed = 1,
  cancelled = 2,
}

export enum CustomerCourseStatus {
  confirmed = 1,
  cancelled = 2,
}

// Helper Classes

export class CourseRunMakeupConfig extends BaseModel {
  mode!: CourseRunMakeupMode;
  courseRuns!: CourseRun[];
  reservations!: Reservation[];

  static override relations(): RelationConfig {
    return {
      courseRuns: { type: CourseRun },
      reservations: { type: Reservation },
    };
  }
}

export class CourseRunPaymentConfig extends BaseModel {
  mode!: CourseRunPaymentMode;
}

export class CourseRunPartialConfig extends BaseModel {
  mode!: CourseRunPartialMode;
}

export class CourseRunReservation extends BaseModel {
  reservation!: Reservation | null;
  courseRun!: CourseRun | null;

  static override relations(): RelationConfig {
    return {
      reservation: { type: Reservation },
    };
  }
}

export class CourseRunCustomer extends BaseModel {
  customer!: Customer | null;
  customerCourse!: CustomerCourse | null;

  static override relations(): RelationConfig {
    return {
      customer: { type: Customer },
      customerCourse: { type: CustomerCourse },
    };
  }
}

export class CustomerCourseReservation extends BaseModel {
  reservation!: Reservation | null;
  reservationCustomer!: ReservationCustomer | null;
  makeupReservationCustomer!: ReservationCustomer | null;

  static override relations(): RelationConfig {
    return {
      reservation: { type: Reservation },
      reservationCustomer: { type: ReservationCustomer },
      makeupReservationCustomer: { type: ReservationCustomer },
    };
  }
}

// CourseRun Model

export class CourseRun extends BaseModel {
  idCourseRun!: number;
  status!: CourseRunStatus;
  makeupConfig!: CourseRunMakeupConfig | null;
  paymentConfig!: CourseRunPaymentConfig | null;
  partialConfig!: CourseRunPartialConfig | null;
  reservations!: CourseRunReservation[];
  course!: Course | null;

  static override relations(): RelationConfig {
    return {
      course: { type: Course },
      makeupConfig: { type: CourseRunMakeupConfig },
      paymentConfig: { type: CourseRunPaymentConfig },
      partialConfig: { type: CourseRunPartialConfig },
      reservations: { type: CourseRunReservation },
    };
  }

  override objectID(): string | null {
    return this.idCourseRun?.toString() ?? null;
  }
}

// CustomerCourse Model

export class CustomerCourse extends BaseModel {
  static modelName(): string {
    return 'customerCourse'
  }

  idCustomerCourse!: number;
  status!: CustomerCourseStatus;
  course!: Course | null;
  courseRun!: CourseRun | null;
  customer!: Customer | null;
  reservations!: CustomerCourseReservation[];
  // Sessions retained from a run the customer was moved out of (same shape as reservations).
  previousReservations!: CustomerCourseReservation[];

  static override relations(): RelationConfig {
    return {
      course: { type: Course },
      courseRun: { type: CourseRun },
      customer: { type: Customer },
      reservations: { type: CustomerCourseReservation },
      previousReservations: { type: CustomerCourseReservation },
    };
  }

  override objectID(): string | null {
    return this.idCustomerCourse?.toString() ?? null;
  }
}

// Main Model

export class Course extends BaseModel {
  static modelName(): string {
    return 'course'
  }

  idCourse!: number;
  name!: string;
  displayName!: string;
  hasImage!: boolean;
  images!: { square?: string };

  override objectID(): string | null {
    return this.idCourse?.toString() ?? null;
  }

  get label(): string {
    return this.displayName;
  }

  get image(): string | null {
    if (this.hasImage) {
      return this.images?.square ?? null;
    }
    return null;
  }

  get initials(): string {
    return (this.name ? this.name.charAt(0) : '').toUpperCase();
  }
}
